import { Fragment, useState } from "react";
import { HubBikeDiscountType, HubDiscountModel, HubModel } from "../utils/Models";
import { getAuthCookie } from "../auth/AuthUtil";
import { useDispatch, useSelector } from "react-redux";
import { getHubSelection, setHubSelection } from "../../features/hubSelection";
import { Box, Button, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { DashboardLoader } from "../utils/LocalStateUtils";
import { Colors, DEFAULT_LOADER_STYLES } from "../../config/Constants";
import { ArrowBack, Close, DoneAll } from "@mui/icons-material";
import { GbkFieldType, GobikesField } from "../utils/GobikesField";
import { betweenValuesValidationFn, parseInvalidFields } from "../utils/ValidationUtils";
import { GobikesApi } from "../../config/Apis";
import { setVendorHubs } from "../../features/vendorProfile";

// Constants and interfaces
interface LocalState {
  loading?: boolean;
  error?: boolean;
  message?: string;
  editMode: boolean;
  discountData: HubDiscountModel
}

// Default value for hubdiscount
const DEFAULT_HUB_DISCOUNT_VALUE = { discountType: HubBikeDiscountType.PERCENT, value: 0 };

// Individual booking details
export function GlobalOffers(props: {
  discount?: HubDiscountModel,
  backFn: (reload: boolean) => void,
  drawerWidth: number
}) {

  // Local states
  const [state, setState] = useState<LocalState>({ editMode: false, discountData: props.discount || DEFAULT_HUB_DISCOUNT_VALUE });

  // Global state
  const dispatch = useDispatch();

  // Parse state and props
  const authCookie = getAuthCookie();
  const selectedHub = useSelector(getHubSelection);
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up('md'));

  // Function to update current offer state
  function updateCurrentOfferData(update: Partial<HubDiscountModel>) {
    setState({ ...state, discountData: { ...(state.discountData || DEFAULT_HUB_DISCOUNT_VALUE), ...update } })
  }

  // Function to save global offer details
  function saveGlobalDiscountDetails() {
    const payload: any = {};
    const invalidFields = parseInvalidFields([
      ...(state.discountData.discountType === HubBikeDiscountType.PERCENT
        ? [{ name: 'Discount percentage', value: state.discountData.value, validateFn: betweenValuesValidationFn(0, 100) }]
        : [])
    ]);
    if (payload.delivery && (!payload.deliveryPrice10 || !payload.deliveryPrice20)) {
      invalidFields.push('Delivery price');
    }
    if (invalidFields.length) {
      setState({
        ...state,
        error: true,
        message: `Please enter these required fields: ${invalidFields.join(', ')}`,
        editMode: state.editMode
      });
      return;
    }
    payload.discount = {
      discountType: state.discountData.discountType,
      value: state.discountData.discountType === HubBikeDiscountType.PERCENT ? (state.discountData.value || 0) / 100 : state.discountData.value,
      minOrder: state.discountData.minOrder,
      maxValue: state.discountData.maxValue
    }
    setState({ ...state, loading: true, message: 'Updating...', editMode: state.editMode });
    (async () => {
      try {
        await GobikesApi.put(`/vendors/hubs/${selectedHub.id}`, payload, { headers: { 'vendor-token': authCookie } });
        (async () => {
          try {
            const { data } = await GobikesApi.get('/vendors/profile-hubs', { params: { cashfreeDetails: true }, headers: { 'vendor-token': authCookie } });
            dispatch(setVendorHubs(data.data.hubs));
            dispatch(setHubSelection(data.data.hubs.find((h: HubModel) => h.id === selectedHub.id)));
            setState({ ...state, loading: false, editMode: false });
          } catch (e) {
            throw new Error("Failed to load hub again");
          }
        })();
      } catch (e: any) {
        const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
        setState({
          ...state, error: true, message: errorMessage, loading: false, editMode: state.editMode,
        })
      }
    })();
  }

  // Delete global discount
  function deleteGlobalDiscountDetails() {
    setState({ ...state, loading: true, message: 'Deleting...', editMode: state.editMode });
    (async () => {
      try {
        await GobikesApi.put(`/vendors/hubs/${selectedHub.id}`, { clearDiscount: true }, { headers: { 'vendor-token': authCookie } });
        (async () => {
          try {
            const { data } = await GobikesApi.get('/vendors/profile-hubs', { params: { cashfreeDetails: true }, headers: { 'vendor-token': authCookie } });
            dispatch(setVendorHubs(data.data.hubs));
            dispatch(setHubSelection(data.data.hubs.find((h: HubModel) => h.id === selectedHub.id)));
            setState({ ...state, loading: false, editMode: false });
          } catch (e) {
            throw new Error("Failed to load hub again");
          }
        })();
      } catch (e: any) {
        const errorMessage = (e.response && e.response.data && e.response.data.message) ? 'Error: ' + e.response.data.message : 'An error occured';
        setState({
          ...state, error: true, message: errorMessage, loading: false, editMode: state.editMode,
        })
      }
    })();
  }

  // Return DOM
  if (state.error || state.loading)
    return (
      <DashboardLoader
        {...state}
        customIcon={<DoneAll className="icon" style={{ color: Colors.primary }} />}
        resetError={state.error ? () => setState({ ...state, error: false, loading: false }) : undefined}
        resetErrorText={'Dismiss'}
        resetErrorIcon={<Close />}
        resetErrorIconPrefix={true}
        styles={DEFAULT_LOADER_STYLES}
      />
    )
  else return (
    <Fragment>
      <div className="gbk-paper-container">

        {/* Empty space at top for phone screen */}
        <Fragment>
          <Box sx={{ display: { xs: 'block', md: 'none' }, height: { xs: '18px', sm: '26px' }, width: 1 }}><br /></Box>
        </Fragment>

        {/* Main container for discount fields */}
        <Paper className="gbk-paper" sx={{ width: '100%' }} elevation={0}>
          <div className="header">
            <Typography className="header-left" fontSize={18} fontWeight={500}>
              {isPhone ? <span className="gbk-icon-span large clickable" onClick={() => props.backFn(false)}><ArrowBack />&nbsp;&nbsp;</span> : ''}
              Global Discount Details
            </Typography>
          </div>
          <div className="content" >
            <div className="gbk-col" style={{ minWidth: 300 }}>
              <GobikesField
                type={GbkFieldType.SELECT}
                label={'Discount type'}
                editMode={state.editMode}
                value={state.discountData.discountType || null}
                selectOptions={[{ value: 'PERCENT', display: 'Percentage' }, { value: 'FLAT', display: 'Flat amount' }]}
                updateFn={(v) => { updateCurrentOfferData({ discountType: v }) }}
              />
              <br />
              {state.editMode ? <br /> : ''}
              <GobikesField
                type={GbkFieldType.NUMBER}
                label={`Discount value (${state.discountData.discountType === HubBikeDiscountType.PERCENT ? '%' : '₹'})`}
                editMode={state.editMode}
                value={state.discountData.value || 0}
                updateFn={(v) => { updateCurrentOfferData({ value: v }) }}
              />
              <br />
              <GobikesField
                type={GbkFieldType.NUMBER}
                label={'Min. order (₹)'}
                editMode={state.editMode}
                value={state.discountData.minOrder || 0}
                updateFn={(v) => { updateCurrentOfferData({ minOrder: v }) }}
              />
              <br />
              <GobikesField
                type={GbkFieldType.NUMBER}
                label={'Max discount (₹)'}
                editMode={state.editMode}
                value={state.discountData.maxValue || 0}
                updateFn={(v) => { updateCurrentOfferData({ maxValue: v }) }}
              />
            </div>
          </div>
        </Paper>

      </div>

      {/* Action buttons */}
      <Box sx={{
        position: 'fixed',
        bottom: 0,
        left: { xs: 0, md: props.drawerWidth },
        width: { xs: '100vw', md: `calc(100vw - ${props.drawerWidth}px)` },
        padding: '20px 40px',
        paddingBottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
        background: '#fff',
        boxShadow: '0px -2px 6px #ddd',
        zIndex: 10
      }}>
        {

          state.editMode
            ? (
              <Fragment>
                <Button className="thick-border" variant="contained" onClick={saveGlobalDiscountDetails}>Save</Button>
                <Button className="thick-border" variant="outlined" sx={{ marginLeft: 2 }} onClick={e => setState({ ...state, editMode: false })}>Cancel</Button>
              </Fragment>
            )
            : (
              <Fragment>
                <Button className="thick-border" variant="contained" onClick={e => setState({ ...state, editMode: true })}>Edit</Button>
                &nbsp;&nbsp;&nbsp;&nbsp;
                <Button className="thick-border" color="error" variant="contained" onClick={deleteGlobalDiscountDetails}>Delete Discount</Button>
              </Fragment>
            )


          //   props.creating ? (
          //     <Fragment>
          //       <Button className="thick-border" variant="contained" onClick={saveOfferDetails}>Create</Button>
          //       <Button className="thick-border" variant="outlined" sx={{ marginLeft: 2 }} onClick={e => props.backFn(true)}>Cancel</Button>
          //       <Typography className='gbk-message error' sx={{ display: 'inline-block', paddingLeft: isPhone ? '0px' : '20px', width: isPhone && currentOfferData.error ? 'calc(100% - 80px)' : 'auto', paddingTop: isPhone && currentOfferData.error ? '10px' : '0px' }}> {currentOfferData.error} </Typography>
          //     </Fragment>
          //   ) : (
          //     editMode
          //       ? (
          //         <Fragment>
          //           <Button className="thick-border" variant="contained" onClick={saveOfferDetails}>Save</Button>
          //           <Button className="thick-border" variant="outlined" sx={{ marginLeft: 2 }} onClick={e => setEditMode(false)}>Cancel</Button>
          //         </Fragment>
          //       )
          //       : (
          //         <Fragment>
          //           <Button className="thick-border" variant="contained" onClick={e => setEditMode(true)}>Edit</Button>
          //         </Fragment>
          //       )
          //   )

        }
      </Box>
    </Fragment >
  )

}